/**
 * CartModal — subscription checkout overlay.
 *
 * Flow:
 *   1. plans   — tier/period selection
 *   2. contact — email + order summary; on submit forwards to Drupal Commerce checkout
 *
 * Mounted globally in App.jsx; opened via useCartStore().openCart().
 */
import { useState, useEffect } from 'react';
import { useCartStore } from '@/stores/cartStore';
import {
  useImpactProduct,
  useAddToCart,
  useCheckout,
  useUpdateCheckout,
} from '@/hooks/useCommerceCart';
import { useLicenses } from '@/hooks/useBilling';
import { useAuth } from '@/components/auth/AuthContext';
import { useUIStore } from '@/stores/uiStore';
import { formatCurrency, cn } from '@/lib/utils';

// VITE_DRUPAL_DIRECT_URL is the absolute Drupal origin (e.g. https://wtsm.ddev.site).
// Used for full-page navigation only — not for API calls (those use the Vite proxy).
const DRUPAL_DIRECT = import.meta.env.VITE_DRUPAL_DIRECT_URL || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function PriceDisplay({ price, periodLabel, className }) {
  if (!price) return <span className={className}>—</span>;
  const amount = parseFloat(price.number ?? price.formatted ?? 0);
  const currency = price.currency_code ?? 'USD';
  return (
    <span className={className}>
      {formatCurrency(Math.round(amount * 100), currency)}
      {periodLabel && (
        <span className="text-xs font-normal text-neutral-500">{periodLabel}</span>
      )}
    </span>
  );
}

// Tier display order — lower-priced tiers first. Tiers not present in API data are skipped.
const PREFERRED_TIER_ORDER = ['Starter', 'Growth', 'Network', 'Scale', 'Enterprise'];

function groupByTierAndPeriod(plans) {
  const map = {};
  for (const plan of plans) {
    const { tier, period, hasFreqSuffix } = plan;
    if (!tier) continue;
    if (!map[tier]) map[tier] = {};
    if (!hasFreqSuffix) {
      map[tier].monthly = plan;
      map[tier].annual = plan;
    } else {
      map[tier][period] = plan;
    }
  }
  return map;
}

// Returns tiers sorted by PREFERRED_TIER_ORDER, limited to what's in the API data.
function deriveTierOrder(tierMap) {
  const available = new Set(Object.keys(tierMap));
  const ordered = PREFERRED_TIER_ORDER.filter((t) => available.has(t));
  // Append any tiers not in PREFERRED_TIER_ORDER (future-proofing)
  for (const t of available) {
    if (!ordered.includes(t)) ordered.push(t);
  }
  return ordered;
}

// Annual savings % relative to paying monthly for 12 months.
// Only computed when annual price is lower than monthly — returns null otherwise.
function annualSavingsPct(tierMap) {
  const tiers = Object.keys(tierMap);
  if (!tiers.length) return null;
  for (const tier of tiers) {
    const mo = parseFloat(tierMap[tier]?.monthly?.price?.number ?? 0);
    const an = parseFloat(tierMap[tier]?.annual?.price?.number ?? 0);
    if (mo > 0 && an > 0 && an < mo) {
      return Math.round(((mo - an) / mo) * 100);
    }
  }
  return null;
}

// Renders HTML feature set from Drupal field_feature_set as a styled checklist.
function FeatureList({ html, inverted }) {
  const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null;
  if (!parser) {
    return (
      <div
        className={cn('text-xs leading-relaxed', inverted ? 'text-parchment/70' : 'text-neutral-500')}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  const doc = parser.parseFromString(html, 'text/html');
  const items = Array.from(doc.querySelectorAll('li')).map((li) => li.textContent?.trim()).filter(Boolean);

  if (items.length === 0) {
    return (
      <div
        className={cn('text-xs leading-relaxed', inverted ? 'text-parchment/70' : 'text-neutral-500')}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-xs">
          <svg
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#c0392b]"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
          </svg>
          <span className={inverted ? 'text-parchment/80' : 'text-neutral-600'}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function CartModal() {
  const {
    isCartOpen, closeCart,
    step, setStep,
    selectedVariation, setSelectedVariation,
    orderUuid, setOrderUuid,
    orderType, setOrderType,
    reset,
  } = useCartStore();

  const { user } = useAuth();
  const addToast = useUIStore((s) => s.addToast);

  const { data: product, isLoading, isError } = useImpactProduct();
  const plans = product?.variations ?? [];
  const tierMap = groupByTierAndPeriod(plans);
  const tierOrder = deriveTierOrder(tierMap);
  const savingsPct = annualSavingsPct(tierMap);

  const { data: licenses } = useLicenses();
  const activeLicense = licenses?.find((l) => l.state === 'active') ?? null;

  const currentTier   = activeLicense?.tier   ?? null;
  const currentPeriod = activeLicense?.period  ?? null;

  const addToCart = useAddToCart();
  const { data: checkoutOrder, isLoading: checkoutLoading } = useCheckout(orderUuid);
  const updateCheckout = useUpdateCheckout();

  const [billingPeriod, setBillingPeriod] = useState(currentPeriod ?? 'monthly');
  const [email, setEmail] = useState(user?.email ?? '');

  useEffect(() => {
    if (user?.email) setEmail((prev) => prev || user.email);
  }, [user?.email]);

  useEffect(() => {
    if (!product || selectedVariation || !currentTier || !currentPeriod) return;
    const match = tierMap[currentTier]?.[currentPeriod];
    if (match) {
      setSelectedVariation(match);
      setBillingPeriod(currentPeriod);
    }
  }, [product, currentTier, currentPeriod]);

  if (!isCartOpen) return null;

  // ── Step 1: plans ───────────────────────────────────────────────────────────

  const handleSelectPlan = (variation) => setSelectedVariation(variation);

  const handlePeriodChange = (period) => {
    setBillingPeriod(period);
    const tier = selectedVariation?.tier;
    if (tier && tierMap[tier]?.[period]) {
      setSelectedVariation(tierMap[tier][period]);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariation) return;
    try {
      const result = await addToCart.mutateAsync({
        variationId: selectedVariation.id,
        variationType: selectedVariation.type,
      });
      if (!result.orderUuid) {
        addToast({ title: 'Cart error', description: 'No order returned from cart.', variant: 'destructive' });
        return;
      }
      setOrderUuid(result.orderUuid);
      setOrderType(result.orderType);
      setEmail(user?.email ?? '');
      setStep('contact');
    } catch {
      // toast shown by mutation onError
    }
  };

  // ── Step 2: contact ─────────────────────────────────────────────────────────

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const resolvedOrderType = orderType ?? checkoutOrder?.data?.type;
    try {
      await updateCheckout.mutateAsync({ orderUuid, orderType: resolvedOrderType, email });
      window.location.href = `${DRUPAL_DIRECT}/checkout`;
    } catch {
      // toast shown by mutation onError
    }
  };

  // ── Close / reset ───────────────────────────────────────────────────────────

  const handleClose = () => {
    closeCart();
    setTimeout(() => {
      reset();
      setEmail('');
    }, 300);
  };

  const stepTitles = {
    plans: 'Choose a Plan',
    contact: 'Contact Information',
  };

  const orderTotal = checkoutOrder?.attributes?.total_price;
  const orderNumber = checkoutOrder?.attributes?.order_number;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className={cn(
        'w-full rounded-2xl border border-neutral-200 bg-document shadow-2xl',
        tierOrder.length >= 5 ? 'max-w-[80rem]' :
        tierOrder.length >= 3 ? 'max-w-[60rem]' : 'max-w-[44rem]',
      )}>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-ink">{stepTitles[step]}</h2>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-neutral-400 transition hover:bg-paper hover:text-ink"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">

          {/* ── Step 1: Plans ──────────────────────────────────────────────── */}
          {step === 'plans' && (
            <div className="space-y-6">

              {/* Billing period toggle */}
              {!isLoading && !isError && plans.length > 0 && (
                <div className="flex items-center justify-center gap-3">
                  <div className="inline-flex rounded-lg border border-neutral-200 bg-paper p-0.5">
                    {['monthly', 'annual'].map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePeriodChange(p)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition',
                          billingPeriod === p
                            ? 'bg-ink text-parchment shadow-sm'
                            : 'text-neutral-500 hover:text-ink',
                        )}
                      >
                        {p === 'monthly' ? 'Monthly' : 'Annual'}
                        {p === 'annual' && savingsPct && (
                          <span className={cn(
                            'rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                            billingPeriod === 'annual'
                              ? 'bg-[#c0392b] text-parchment'
                              : 'bg-[#c0392b]/10 text-[#c0392b]',
                          )}>
                            Save {savingsPct}%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Skeleton */}
              {isLoading && (
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-64 animate-pulse rounded-2xl bg-paper" />
                  ))}
                </div>
              )}

              {/* Error */}
              {isError && (
                <div className="rounded-xl border border-neutral-200 bg-paper px-4 py-8 text-center text-sm text-neutral-500">
                  Unable to load subscription plans. Please try again.
                </div>
              )}

              {/* Empty */}
              {!isLoading && !isError && plans.length === 0 && (
                <div className="rounded-xl border border-neutral-200 bg-paper px-4 py-8 text-center text-sm text-neutral-500">
                  No subscription plans available at this time.
                </div>
              )}

              {/* Tier cards */}
              {!isLoading && plans.length > 0 && (
                <fieldset>
                  <legend className="sr-only">Choose a plan</legend>
                  <div className={cn(
                    'grid gap-4',
                    tierOrder.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' :
                    tierOrder.length === 2 ? 'grid-cols-2' :
                    tierOrder.length === 3 ? 'grid-cols-3' :
                    tierOrder.length === 4 ? 'grid-cols-4' :
                    'grid-cols-5',
                  )}>
                    {tierOrder.map((tier, idx) => {
                      const variation = tierMap[tier]?.[billingPeriod];
                      const monthlyVariation = tierMap[tier]?.['monthly'];
                      const isSelected = variation && selectedVariation?.id === variation.id;
                      const isCurrent = tier === currentTier && billingPeriod === currentPeriod;
                      // First tier of 2 gets "Most popular"; middle tier of 3+
                      const isPopular = tierOrder.length === 2
                        ? idx === 0
                        : idx === Math.floor((tierOrder.length - 1) / 2);

                      return (
                        <label
                          key={tier}
                          className={cn(
                            'relative flex cursor-pointer flex-col rounded-2xl border-2 p-5 transition-all',
                            !variation && 'pointer-events-none opacity-40',
                            isSelected
                              ? 'border-ink bg-ink text-parchment shadow-lg scale-[1.02]'
                              : isPopular
                                ? 'border-neutral-300 bg-paper hover:border-ink/40'
                                : 'border-neutral-200 bg-paper hover:border-neutral-300',
                          )}
                        >
                          <input
                            type="radio"
                            name="tier"
                            value={tier}
                            disabled={!variation}
                            checked={!!isSelected}
                            onChange={() => variation && handleSelectPlan(variation)}
                            className="sr-only"
                          />

                          {/* Badge row */}
                          <div className="mb-3 flex items-center gap-2">
                            {isCurrent && (
                              <span className={cn(
                                'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                isSelected ? 'bg-parchment/20 text-parchment' : 'bg-ink text-parchment',
                              )}>
                                Current
                              </span>
                            )}
                            {isPopular && !isCurrent && (
                              <span className={cn(
                                'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                isSelected ? 'bg-[#c0392b] text-parchment' : 'bg-[#c0392b]/10 text-[#c0392b]',
                              )}>
                                Most popular
                              </span>
                            )}
                          </div>

                          {/* Tier name */}
                          <p className={cn(
                            'text-base font-bold leading-tight',
                            isSelected ? 'text-parchment' : 'text-ink',
                          )}>
                            {tier}
                          </p>

                          {/* Price */}
                          <div className="mt-3 mb-4">
                            {variation ? (
                              <>
                                <div className="flex items-baseline gap-1">
                                  <PriceDisplay
                                    price={variation.price}
                                    className={cn(
                                      'text-2xl font-bold',
                                      isSelected ? 'text-parchment' : 'text-ink',
                                    )}
                                  />
                                  <span className={cn(
                                    'text-xs',
                                    isSelected ? 'text-parchment/60' : 'text-neutral-400',
                                  )}>
                                    /mo
                                  </span>
                                </div>
                                {billingPeriod === 'annual' && monthlyVariation?.id !== variation?.id && (
                                  <p className={cn(
                                    'mt-0.5 text-xs',
                                    isSelected ? 'text-parchment/60' : 'text-neutral-400',
                                  )}>
                                    billed annually
                                    {monthlyVariation?.price && parseFloat(monthlyVariation.price.number) > parseFloat(variation.price.number) && (
                                      <span className={cn(
                                        'ml-1.5 line-through',
                                        isSelected ? 'text-parchment/40' : 'text-neutral-300',
                                      )}>
                                        <PriceDisplay price={monthlyVariation.price} className="inline" />/mo
                                      </span>
                                    )}
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-sm text-neutral-400">—</span>
                            )}
                          </div>

                          {/* Divider */}
                          <div className={cn(
                            'mb-4 border-t',
                            isSelected ? 'border-parchment/20' : 'border-neutral-200',
                          )} />

                          {/* Features */}
                          {variation?.features ? (
                            <FeatureList html={variation.features} inverted={isSelected} />
                          ) : (
                            <p className={cn(
                              'text-xs',
                              isSelected ? 'text-parchment/50' : 'text-neutral-400',
                            )}>
                              Contact us for details.
                            </p>
                          )}

                          {/* Selection indicator */}
                          <div className={cn(
                            'mt-auto pt-4 flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-semibold transition',
                            isSelected
                              ? 'bg-parchment/15 text-parchment'
                              : 'bg-neutral-100 text-neutral-500',
                          )}>
                            {isSelected ? (
                              <>
                                <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
                                </svg>
                                Selected
                              </>
                            ) : (
                              'Select plan'
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              )}

              {/* CTA */}
              {!isLoading && !isError && plans.length > 0 && (
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedVariation || addToCart.isPending}
                  className="w-full rounded-xl bg-[#c0392b] py-3 text-sm font-semibold text-parchment transition hover:bg-[#a93226] active:scale-95 disabled:opacity-40"
                >
                  {addToCart.isPending
                    ? 'Adding to cart…'
                    : selectedVariation
                      ? `Continue with ${selectedVariation.tier} →`
                      : 'Select a plan to continue'}
                </button>
              )}
            </div>
          )}

          {/* ── Step 2: Contact ────────────────────────────────────────────── */}
          {step === 'contact' && (
            <form onSubmit={handleContactSubmit} className="space-y-5">
              {/* Order summary */}
              <div className="rounded-xl border border-neutral-200 bg-paper divide-y divide-neutral-200">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-neutral-500">Plan</span>
                  <span className="text-sm font-medium text-ink">{selectedVariation?.title}</span>
                </div>
                {selectedVariation?.billingSchedule?.label && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-neutral-500">Billing</span>
                    <span className="text-sm text-ink">{selectedVariation.billingSchedule.label}</span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-neutral-500">Amount</span>
                  {checkoutLoading ? (
                    <span className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                  ) : (
                    <PriceDisplay
                      price={orderTotal ?? selectedVariation?.price}
                      periodLabel={selectedVariation?.periodLabel}
                      className="flex items-baseline gap-0.5 text-sm font-semibold text-ink"
                    />
                  )}
                </div>
                {orderNumber && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-neutral-500">Order #</span>
                    <span className="font-mono text-xs text-neutral-500">{orderNumber}</span>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="checkout-email" className="block text-sm font-medium text-neutral-500">
                  Email address
                </label>
                <input
                  id="checkout-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2.5 text-sm text-ink placeholder-neutral-400 outline-none transition focus:border-[#003cc5] focus:ring-1 focus:ring-[#003cc5]"
                />
                <p className="text-xs text-neutral-400">Your receipt and subscription details will be sent here.</p>
              </div>

              <button
                type="submit"
                disabled={!email || updateCheckout.isPending || checkoutLoading}
                className="w-full rounded-xl bg-[#003cc5] py-3 text-sm font-semibold text-parchment transition hover:bg-[#0036b1] active:scale-95 disabled:opacity-50"
              >
                {updateCheckout.isPending ? 'Processing…' : 'Proceed to Payment'}
              </button>

              <button
                type="button"
                onClick={() => setStep('plans')}
                disabled={updateCheckout.isPending}
                className="w-full rounded-xl border border-neutral-200 py-2.5 text-sm text-neutral-500 transition hover:bg-paper disabled:opacity-40"
              >
                Back
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
